/**
 * The web-chat widget's appearance contract, mirrored from the operations DTO
 * (`web-chat-appearance.dto.ts`) plus the token resolution the embedded widget
 * performs at runtime (`web-chat-loader.ts`).
 *
 * The resolver is duplicated here rather than shared because the widget ships
 * as a dependency-free string of vanilla JS served by operations — there is no
 * module the browser app could import. Keeping the rules in one small, tested
 * file makes the duplication auditable: if the two ever drift, the settings
 * preview stops matching the live widget, which is the bug this exists to
 * prevent. Any change to the palette or contrast rules belongs in both.
 */

export type WebChatThemeMode = 'SYSTEM' | 'LIGHT' | 'DARK';
export type WebChatLauncherSide = 'LEFT' | 'RIGHT';
export type WebChatHeaderTextColor = 'AUTO' | 'BLACK' | 'WHITE';
export type WebChatBackgroundType = 'SOLID' | 'GRADIENT' | 'IMAGE';

export interface WebChatLauncherConfig {
  side?: WebChatLauncherSide;
  sideSpacing?: number;
  bottomSpacing?: number;
  logoUrl?: string;
  hidden?: boolean;
}

export interface WebChatBackgroundConfig {
  type?: WebChatBackgroundType;
  color?: string;
  gradientFrom?: string;
  gradientTo?: string;
  imageUrl?: string;
  fade?: boolean;
}

export interface WebChatAppearance {
  title?: string;
  greeting?: string;
  themeColor?: string;
  themeMode?: WebChatThemeMode;
  headerTextColor?: WebChatHeaderTextColor;
  headerLogoUrl?: string;
  launcher?: WebChatLauncherConfig;
  background?: WebChatBackgroundConfig;
}

export const DEFAULT_THEME_COLOR = '#0a0b0d';
export const DEFAULT_TITLE = 'Iris';
export const DEFAULT_SIDE_SPACING = 20;
export const DEFAULT_BOTTOM_SPACING = 20;

/** Neutral ramps per mode — the brand colour is mode-independent. */
const PALETTE = {
  light: {
    surface: '#fafaf8',
    surfaceAlt: '#f2f1ec',
    text: '#0a0b0d',
    muted: '#6b6b70',
    border: '#e3e2dc',
    bubbleBg: '#ffffff',
    bubbleText: '#0a0b0d',
  },
  dark: {
    surface: '#1c1c1f',
    surfaceAlt: '#232327',
    text: '#ececf1',
    muted: '#a5a5ad',
    border: '#33333b',
    bubbleBg: '#2a2a31',
    bubbleText: '#ececf1',
  },
} as const;

export const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function parseHex(hex: string | undefined): [number, number, number] | null {
  let h = String(hex ?? '').replace('#', '');
  if (h.length === 3) h = `${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
  if (h.length !== 6) return null;
  const n = Number.parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** WCAG relative luminance. */
function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((x) => {
    const s = x / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Black or white on the brand colour, whichever reads better. A pale brand
 * colour with forced white text is unreadable on the customer's own site,
 * where we would never see it — so AUTO is the default.
 */
export function resolveOnTheme(appearance: WebChatAppearance): string {
  const pref = appearance.headerTextColor;
  if (pref === 'BLACK') return '#111111';
  if (pref === 'WHITE') return '#ffffff';
  const rgb = parseHex(appearance.themeColor || DEFAULT_THEME_COLOR);
  if (!rgb) return '#ffffff';
  return luminance(rgb) > 0.179 ? '#111111' : '#ffffff';
}

export function resolveMode(
  appearance: WebChatAppearance,
  systemPrefersDark: boolean,
): 'light' | 'dark' {
  const mode = appearance.themeMode ?? 'SYSTEM';
  if (mode === 'DARK') return 'dark';
  if (mode === 'LIGHT') return 'light';
  return systemPrefersDark ? 'dark' : 'light';
}

/** Phones always get bottom-right — there is no room for a left-side panel. */
export function resolveSide(appearance: WebChatAppearance): WebChatLauncherSide {
  return appearance.launcher?.side === 'LEFT' ? 'LEFT' : 'RIGHT';
}

export function clampSpacing(value: number | undefined, fallback: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.max(0, Math.min(200, n));
}

/**
 * Config URLs land inside a CSS `url()`, so anything that could terminate the
 * function or the declaration is rejected rather than escaped. The API already
 * validates these as https; this is the second line of defence.
 */
export function safeUrl(url: string | undefined): string | null {
  const u = String(url ?? '');
  if (!u) return null;
  if (/["');\n\r]/.test(u)) return null;
  if (!u.startsWith('https://')) return null;
  return u;
}

const solidLayer = (color: string) => `linear-gradient(${color},${color})`;

/**
 * The conversation canvas. Every variant is a background *layer* so the
 * optional fade composites over any of them with one uniform rule; the first
 * layer paints on top, so the fade leads.
 */
export function resolveCanvas(appearance: WebChatAppearance, mode: 'light' | 'dark'): string {
  const bg = appearance.background ?? {};
  const type = bg.type ?? 'SOLID';
  let base: string;
  if (type === 'GRADIENT') {
    base = `linear-gradient(160deg,${bg.gradientFrom || '#f7f7f9'},${bg.gradientTo || '#e4e4ec'})`;
  } else if (type === 'IMAGE' && safeUrl(bg.imageUrl)) {
    base = `url("${safeUrl(bg.imageUrl)}")`;
  } else if (type === 'SOLID' && bg.color) {
    base = solidLayer(bg.color);
  } else {
    base = solidLayer(PALETTE[mode].surfaceAlt);
  }
  if (bg.fade) {
    base = `linear-gradient(to bottom,rgba(0,0,0,0) 35%,${PALETTE[mode].surface} 100%),${base}`;
  }
  return base;
}

export interface WebChatTokens {
  theme: string;
  onTheme: string;
  surface: string;
  surfaceAlt: string;
  text: string;
  muted: string;
  border: string;
  bubbleBg: string;
  bubbleText: string;
  canvas: string;
}

/** Everything the preview needs to paint a faithful copy of the live widget. */
export function resolveTokens(
  appearance: WebChatAppearance,
  systemPrefersDark: boolean,
): WebChatTokens {
  const mode = resolveMode(appearance, systemPrefersDark);
  return {
    ...PALETTE[mode],
    theme: appearance.themeColor || DEFAULT_THEME_COLOR,
    onTheme: resolveOnTheme(appearance),
    canvas: resolveCanvas(appearance, mode),
  };
}

/**
 * Per-field sanitisers mirroring `WebChatAppearanceDto`'s validators. Each
 * returns the value to send, or `undefined` to omit the key.
 *
 * These exist because `config` used to be a freeform JSON blob: a widget in the
 * wild can hold a key we never shipped (`launcherText`, `position`) *or* a
 * known key with a value the new DTO rejects (`themeColor: "blue"` — a valid
 * CSS colour, but not the canonical hex the settings colour picker round-trips).
 * The API validates with `forbidNonWhitelisted` plus `Matches`/`IsUrl`/`Min`/
 * `Max`/enums, so echoing either back would 400 the whole save — including a
 * save that only renames the widget. Anything unsendable is dropped here rather
 * than failing the request.
 */
type Sanitiser = (value: unknown) => unknown;

const asText =
  (maxLength: number): Sanitiser =>
  (value) => {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    // Truncate rather than drop: a too-long title still expresses intent.
    return trimmed === '' ? undefined : trimmed.slice(0, maxLength);
  };

const asHex: Sanitiser = (value) =>
  typeof value === 'string' && HEX_COLOR.test(value.trim()) ? value.trim() : undefined;

const asHttpsUrl: Sanitiser = (value) =>
  typeof value === 'string' && safeUrl(value.trim()) ? value.trim() : undefined;

const asEnum =
  (allowed: readonly string[]): Sanitiser =>
  (value) =>
    typeof value === 'string' && allowed.includes(value) ? value : undefined;

/** Clamped, not dropped — the widget clamps the same way at runtime. */
const asSpacing: Sanitiser = (value) => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(Math.max(0, Math.min(200, n)));
};

const asBoolean: Sanitiser = (value) => (typeof value === 'boolean' ? value : undefined);

const APPEARANCE_FIELDS: Record<string, Sanitiser> = {
  title: asText(60),
  greeting: asText(300),
  themeColor: asHex,
  themeMode: asEnum(['SYSTEM', 'LIGHT', 'DARK']),
  headerTextColor: asEnum(['AUTO', 'BLACK', 'WHITE']),
  headerLogoUrl: asHttpsUrl,
};

const LAUNCHER_FIELDS: Record<string, Sanitiser> = {
  side: asEnum(['LEFT', 'RIGHT']),
  sideSpacing: asSpacing,
  bottomSpacing: asSpacing,
  logoUrl: asHttpsUrl,
  hidden: asBoolean,
};

const BACKGROUND_FIELDS: Record<string, Sanitiser> = {
  type: asEnum(['SOLID', 'GRADIENT', 'IMAGE']),
  color: asHex,
  gradientFrom: asHex,
  gradientTo: asHex,
  imageUrl: asHttpsUrl,
  fade: asBoolean,
};

/**
 * Reduce the edited appearance to exactly what the API accepts — known keys
 * only, each with a value its DTO validator will pass. See the note on
 * `Sanitiser` for why both halves are necessary.
 *
 * Empty nested objects collapse away too: the widget falls back per token, so
 * an absent key and a default value mean the same thing to it.
 */
export function pruneAppearance(appearance: WebChatAppearance): WebChatAppearance {
  const pick = (
    source: object | undefined,
    fields: Record<string, Sanitiser>,
  ): Record<string, unknown> | undefined => {
    if (!source) return undefined;
    const out: Record<string, unknown> = {};
    for (const [key, sanitise] of Object.entries(fields)) {
      const raw = (source as Record<string, unknown>)[key];
      if (raw === undefined || raw === null) continue;
      const value = sanitise(raw);
      if (value !== undefined) out[key] = value;
    }
    return Object.keys(out).length ? out : undefined;
  };

  const result: Record<string, unknown> = {
    ...(pick(appearance, APPEARANCE_FIELDS) ?? {}),
  };
  const launcher = pick(appearance.launcher, LAUNCHER_FIELDS);
  const background = pick(appearance.background, BACKGROUND_FIELDS);
  if (launcher) result.launcher = launcher;
  if (background) result.background = background;
  return result as WebChatAppearance;
}
